import React, { useState, useEffect } from 'react';
import {
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Box,
    Chip,
    IconButton
} from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import { reportAPI, bookingAPI } from '../services/api';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import { formatTimeRange } from '../utils/timeUtils';

const logoColors = {
    primaryBlue: '#3f51b5',
    brightYellow: '#c4d835',
    darkBlue: '#2c387e',
    lightYellow: '#d4e157',
};

const DuesPage = () => {
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sendingId, setSendingId] = useState(null);

    const fetchDues = async () => {
        setLoading(true);
        try {
            const response = await reportAPI.getBookingsWithDues();
            setBookings(response.data || []);
        } catch (error) {
            toast.error('Failed to fetch bookings with dues');
            console.error('Error fetching dues:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDues();
    }, []);

    const handleSendReminder = async (booking) => {
        try {
            setSendingId(booking.id);
            await bookingAPI.sendDueReminder(booking.id);
            toast.success(`Reminder sent to ${booking.customerName}`);
        } catch (error) {
            toast.error('Failed to send reminder: ' + (error.response?.data?.message || error.message));
            console.error('Error sending due reminder:', error);
        } finally {
            setSendingId(null);
        }
    };

    const totalOutstanding = bookings.reduce((sum, b) => sum + (b.amountDue || 0), 0);

    if (loading) {
        return (
            <Paper sx={{ p: 3, m: 2, backgroundColor: logoColors.darkBlue, color: logoColors.brightYellow }}>
                <Typography color="inherit">Loading dues...</Typography>
            </Paper>
        );
    }

    return (
        <Paper elevation={3} sx={{
            p: 3,
            m: 2,
            backgroundColor: logoColors.darkBlue,
            color: logoColors.brightYellow
        }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="h5" sx={{ color: logoColors.brightYellow, fontWeight: 'bold' }}>
                    Outstanding Dues ({bookings.length})
                </Typography>
                <Chip
                    label={`Total Owed: PKR ${totalOutstanding.toFixed(2)}`}
                    color={totalOutstanding > 0 ? 'warning' : 'success'}
                    sx={{ fontWeight: 'bold', fontSize: '0.95rem', py: 2 }}
                />
            </Box>

            <TableContainer sx={{
                backgroundColor: logoColors.primaryBlue,
                borderRadius: 1,
                overflowX: 'auto'
            }}>
                <Table sx={{ minWidth: 900 }}>
                    <TableHead>
                        <TableRow sx={{ backgroundColor: logoColors.brightYellow }}>
                            <TableCell sx={{ color: logoColors.darkBlue, fontWeight: 'bold' }}><strong>ID</strong></TableCell>
                            <TableCell sx={{ color: logoColors.darkBlue, fontWeight: 'bold' }}><strong>Customer</strong></TableCell>
                            <TableCell sx={{ color: logoColors.darkBlue, fontWeight: 'bold' }}><strong>Contact</strong></TableCell>
                            <TableCell sx={{ color: logoColors.darkBlue, fontWeight: 'bold' }}><strong>Opponent</strong></TableCell>
                            <TableCell sx={{ color: logoColors.darkBlue, fontWeight: 'bold' }}><strong>Date</strong></TableCell>
                            <TableCell sx={{ color: logoColors.darkBlue, fontWeight: 'bold' }}><strong>Time</strong></TableCell>
                            <TableCell sx={{ color: logoColors.darkBlue, fontWeight: 'bold' }}><strong>Court</strong></TableCell>
                            <TableCell sx={{ color: logoColors.darkBlue, fontWeight: 'bold' }}><strong>Total</strong></TableCell>
                            <TableCell sx={{ color: logoColors.darkBlue, fontWeight: 'bold' }}><strong>Paid</strong></TableCell>
                            <TableCell sx={{ color: logoColors.darkBlue, fontWeight: 'bold' }}><strong>Due</strong></TableCell>
                            <TableCell sx={{ color: logoColors.darkBlue, fontWeight: 'bold' }}><strong>Status</strong></TableCell>
                            <TableCell sx={{ color: logoColors.darkBlue, fontWeight: 'bold' }}><strong>Actions</strong></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {bookings.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={12} sx={{ color: logoColors.brightYellow, textAlign: 'center', py: 4 }}>
                                    No outstanding dues. Everyone's paid up!
                                </TableCell>
                            </TableRow>
                        ) : (
                            bookings.map((booking, index) => (
                                <TableRow
                                    key={booking.id}
                                    hover
                                    sx={{
                                        backgroundColor: index % 2 === 0 ? logoColors.primaryBlue : logoColors.darkBlue,
                                        '&:hover': { backgroundColor: logoColors.lightYellow + '20' }
                                    }}
                                >
                                    <TableCell sx={{ color: logoColors.brightYellow }}>{booking.id}</TableCell>
                                    <TableCell sx={{ color: logoColors.brightYellow }}>{booking.customerName}</TableCell>
                                    <TableCell sx={{ color: logoColors.brightYellow }}>
                                        {booking.customerEmail}<br />
                                        <small style={{ opacity: 0.8 }}>{booking.customerPhone}</small>
                                    </TableCell>
                                    <TableCell sx={{ color: logoColors.brightYellow }}>{booking.opponentName || '-'}</TableCell>
                                    <TableCell sx={{ color: logoColors.brightYellow }}>{dayjs(booking.bookingDate).format('MMM DD, YYYY')}</TableCell>
                                    <TableCell sx={{ color: logoColors.brightYellow }}>{formatTimeRange(booking.startTime, booking.endTime)}</TableCell>
                                    <TableCell sx={{ color: logoColors.brightYellow }}>Court {booking.courtNumber}</TableCell>
                                    <TableCell sx={{ color: logoColors.brightYellow }}>PKR {booking.totalAmount}</TableCell>
                                    <TableCell sx={{ color: logoColors.brightYellow }}>
                                        PKR {(booking.paidCashAmount || 0) + (booking.paidOnlineAmount || 0)}
                                        <br />
                                        <small style={{ opacity: 0.8 }}>Cash {booking.paidCashAmount || 0} / Online {booking.paidOnlineAmount || 0}</small>
                                    </TableCell>
                                    <TableCell>
                                        <Chip label={`PKR ${booking.amountDue}`} color="warning" size="small" />
                                    </TableCell>
                                    <TableCell>
                                        <Chip label={booking.status} color={booking.status === 'CONFIRMED' ? 'success' : 'default'} size="small" />
                                    </TableCell>
                                    <TableCell>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            startIcon={<SendIcon />}
                                            disabled={sendingId === booking.id}
                                            onClick={() => handleSendReminder(booking)}
                                            sx={{
                                                backgroundColor: logoColors.brightYellow,
                                                color: logoColors.darkBlue,
                                                fontWeight: 'bold',
                                                textTransform: 'none',
                                                '&:hover': { backgroundColor: logoColors.lightYellow }
                                            }}
                                        >
                                            {sendingId === booking.id ? 'Sending...' : 'Send Reminder'}
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );
};

export default DuesPage;
